import { fa, toman } from './fa';
import { isClaimed } from './arrivals';

/*
 * Orders that arrived and nobody has taken — from a kiosk, or from a waiter.
 *
 * A kiosk order reaches this till by replication and lands in the books
 * immediately. That is correct and it is not enough — a sale filed in history is
 * not a person being told food needs making, and before this screen existed a
 * shop with a cashier had nowhere a kiosk order appeared at all. The customer
 * had paid, held a receipt, and waited for something nobody knew to make.
 *
 * A waiter's round is the same job wearing different clothes, and it was worse:
 * a branch configured with a till tells the tablet NOT to fire the kitchen
 * ticket, because the round is the till's to turn into one — and nothing here
 * listed rounds at all. The order was recorded, the waiter was told the kitchen
 * had it, and no screen in the shop showed it. The two kinds are on one list
 * because to the person at the counter they are one job: something came in,
 * tell the kitchen.
 *
 * They are NOT interchangeable, though, and the difference is money. A kiosk
 * order is paid; a table round is guests still sitting there. A till that
 * cannot tell them apart shows a total for a bill nobody has settled.
 */

export type Arrival = {
  id: string;
  /** "kiosk" — already paid. "table" — a waiter's round, nobody has paid yet. */
  kind?: 'kiosk' | 'table';
  /** Where the food goes. Set for a table round only. */
  tableLabel?: string;
  number: string;
  issuedAt: string;
  totalRial: number;
  reference: string;
  waitingMinutes: number;
  takenBy?: string;
  takenMinutes?: number;
  /*
   * Whether the money is already in. Absent from an older agent, where `kind`
   * is the only thing that answers it — a kiosk order is paid at the screen and
   * a table's round is guests still sitting there.
   */
  paid?: boolean;
  /*
   * Whether a person has claimed it — which `takenBy` does NOT answer. That
   * field is a user id, and a till whose session carries an empty one claims
   * orders with `seen_by = ''`. Absent from an older agent, which reads as
   * unclaimed and therefore still worth announcing: the safe direction.
   */
  taken?: boolean;
  /*
   * Whether the kitchen already has it, and from whom.
   *
   * On a branch that fires automatically the answer is "yes, a second after it
   * arrived" for every order — which is why these rounds used to be dropped
   * from this list entirely and the screen sat empty all day. They are listed
   * now, and this is what distinguishes one already being made from one still
   * waiting for the cashier.
   *
   * Absent from an older agent, which reads as not fired — the safe direction,
   * because it leaves the fire button available rather than hiding it on an
   * order nobody has sent.
   */
  firedAt?: string;
  firedBy?: string;
  /*
   * The kitchen ticket this order became, when it has one.
   *
   * What the reprint action hangs off. Absent on an order nobody has fired,
   * and on an agent too old to send it — in both cases the button is simply
   * not offered, rather than offered and broken.
   */
  ticketId?: string;
  /*
   * The check a table round belongs to.
   *
   * What the payment action needs. Absent on a kiosk order, which is already
   * paid and owes nothing — so the button is simply not offered there.
   */
  checkId?: string;
  lines: { name: string; quantity: number; lineTotalRial: number; modifiers: { name: string }[] }[];
};

/*
 * Where an order came from, said in words.
 *
 * Asked for directly: a cashier looking at this list needs to know whether the
 * order came off the kiosk screen or a waiter's tablet, because it changes who
 * they talk to about it. `kind` already carried the answer and nothing
 * rendered it — the screen distinguished the two only by whether it showed a
 * table label, which is not a thing anyone reads as a source.
 */
export function sourceLabel(kind: Arrival['kind']): { text: string; icon: string } {
  switch (kind) {
    case 'kiosk':
      return { text: 'کیوسک', icon: '▣' };
    case 'table':
      return { text: 'میز (تبلت)', icon: '◧' };
    default:
      // An agent too old to say. Better than inventing a source.
      return { text: 'نامشخص', icon: '•' };
  }
}

/** True when the kitchen already has this order. */
export function alreadyFired(a: Arrival): boolean {
  return Boolean(a.firedAt);
}

/*
 * How long it has waited decides how it looks.
 *
 * A queue that backfills quietly on reconnect is how a lunchtime rush ends with
 * six orders nobody saw — so an order that arrived while the till was offline
 * must not look like one that arrived just now. The thresholds are deliberately
 * coarse: a cashier is reading this at a glance across a counter, not comparing
 * timestamps.
 */
function urgency(minutes: number): { border: string; bg: string; label: string | null } {
  if (minutes >= 10) {
    return { border: 'var(--luma-danger, #A8352C)', bg: 'rgba(168,53,44,0.06)', label: `${fa(minutes)} دقیقه منتظر` };
  }
  if (minutes >= 3) {
    return { border: 'var(--luma-warn, #97671A)', bg: 'rgba(151,103,26,0.06)', label: `${fa(minutes)} دقیقه منتظر` };
  }
  return { border: 'var(--luma-line, #D6D6CC)', bg: 'transparent', label: null };
}

export function ArrivalsScreen({
  arrivals, staleUnclaimed = 0, onTake, onFire, onOpen, onBack, busyId,
}: {
  arrivals: Arrival[];
  /*
   * Unclaimed orders from before this shift, which the agent leaves off the
   * list on purpose.
   *
   * Shown as a number and nothing more. They are not work waiting to be done —
   * nobody is standing there and the food was never made — but they are also
   * not nothing, so the one thing this must not do is put them back in the
   * queue a cashier reads during service.
   */
  staleUnclaimed?: number;
  onTake: (id: string) => void;
  onFire: (id: string) => void;
  /*
   * Opening one.
   *
   * The card had no tap target at all, which only became a dead end once
   * fired orders started being listed: those have no button on them — fire is
   * withheld because they are already fired, claiming is kiosk-only — so
   * tapping an order did nothing whatsoever. Reported from a shop.
   */
  onOpen: (a: Arrival) => void;
  onBack: () => void;
  busyId: string | null;
}) {
  return (
    <div style={{ flex: 1, padding: 18, display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={onBack}
          style={{
            border: '1px solid var(--luma-line, #D6D6CC)', background: 'transparent',
            borderRadius: 10, padding: '10px 16px', fontSize: 15, cursor: 'pointer',
          }}
        >
          بازگشت
        </button>
        <h2 style={{ margin: 0, fontSize: 20 }}>سفارش‌های ورودی</h2>
        {arrivals.length > 0 && (
          <span
            style={{
              background: 'var(--luma-accent, #1C6B62)', color: '#fff',
              borderRadius: 999, padding: '2px 12px', fontSize: 14,
            }}
          >
            {fa(arrivals.length)}
          </span>
        )}
      </div>

      {arrivals.length === 0 ? (
        /*
         * An empty list is the normal state and must read as "nothing is
         * waiting", not as a screen that failed to load. A cashier glancing at
         * a blank panel mid-rush should not have to wonder which it is.
         */
        <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--luma-muted, #6C7276)' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 17, marginBottom: 6 }}>سفارش ورودی‌ای در انتظار نیست</div>
            <div style={{ fontSize: 14 }}>سفارش‌های کیوسک و میزها این‌جا ظاهر می‌شوند</div>
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {arrivals.map((a) => {
            const taken = isClaimed(a);
            const u = taken
              /*
               * A claimed order sits quietly rather than leaving.
               *
               * If it vanished, an order somebody claimed and then abandoned —
               * the till crashed, they walked off — would look handled to the
               * whole shop while nobody was making it. Quiet enough not to
               * compete with what is still waiting, present enough that anyone
               * can see nothing has come out of the kitchen.
               */
              ? { border: 'var(--luma-line, #D6D6CC)', bg: 'transparent', label: null }
              : urgency(a.waitingMinutes);
            return (
              <div
                key={a.id}
                /*
                 * The whole card opens the order.
                 *
                 * A button-only card has nothing to press on a fired order,
                 * and that is most of them on a branch that fires
                 * automatically — so the screen listed work a cashier could
                 * look at and not touch. role/tabIndex because this is a
                 * control now, not a panel.
                 */
                role="button"
                tabIndex={0}
                onClick={() => onOpen(a)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpen(a);
                  }
                }}
                style={{
                  border: `1px solid ${u.border}`, borderInlineStartWidth: 4,
                  background: u.bg, borderRadius: 12, padding: '14px 16px',
                  display: 'flex', alignItems: 'flex-start', gap: 16,
                  opacity: taken ? 0.62 : 1, cursor: 'pointer', textAlign: 'start',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/*
                    Two lines, not one.
                    Identity and money belong together and get read first; who
                    claimed it and how long it has waited are a second thought.
                    Crammed onto one baseline they ran together into a sentence
                    nobody could scan — «۱,۳۲۰ تومان device:C01 — ۱۸ دقیقه پیش
                    دریافت کرد» reads as one phrase, and our user said so.
                  */}
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                    {/*
                      The source, before the identity.
                      A cashier reading this list has to know where an order
                      came from — the kiosk screen or a waiter's tablet —
                      because it decides who they talk to about it. Icon and
                      word together: the icon is what the eye finds on a
                      crowded list, the word is what removes any doubt about
                      which icon is which.
                    */}
                    <span
                      style={{
                        fontSize: 12.5, fontWeight: 700, borderRadius: 999,
                        padding: '2px 9px', whiteSpace: 'nowrap',
                        color: 'var(--luma-muted)',
                        background: 'var(--luma-accent-tint)',
                        border: '1px solid var(--luma-line-strong)',
                      }}
                    >
                      <span aria-hidden="true" style={{ marginInlineEnd: 5 }}>
                        {sourceLabel(a.kind).icon}
                      </span>
                      {sourceLabel(a.kind).text}
                    </span>
                    <strong style={{ fontSize: 17 }}>
                      {a.kind === 'table' && a.tableLabel ? a.tableLabel : fa(a.number)}
                    </strong>
                    {/*
                      Said only when it is true. A kiosk order is paid and needs
                      no label; an open table does, because the number beside it
                      is a running total and not a bill anybody has settled.
                    */}
                    {a.kind === 'table' && (
                      <span
                        style={{
                          fontSize: 13, fontWeight: 700, borderRadius: 999,
                          padding: '2px 8px', color: 'var(--luma-warn, #97671A)',
                          background: 'rgba(151,103,26,0.10)',
                        }}
                      >
                        سر میز — تسویه نشده
                      </span>
                    )}
                    {/*
                      Already with the kitchen.
                      Said plainly, because the whole reason these orders are
                      on the list now is that they used to vanish the instant
                      the server fired them. Listed and unmarked would be
                      worse than hidden: a cashier would fire it again.
                    */}
                    {alreadyFired(a) && (
                      <span
                        style={{
                          fontSize: 13, fontWeight: 700, borderRadius: 999,
                          padding: '2px 8px', color: 'var(--luma-ok, #2F6B3A)',
                          background: 'rgba(47,107,58,0.10)',
                        }}
                      >
                        {a.firedBy === 'system:auto' ? 'به آشپزخانه رفت (خودکار)' : 'به آشپزخانه رفت'}
                      </span>
                    )}
                    {/* toman() carries its own unit. It used to be followed by a
                        literal «تومان» as well, so every amount on this screen
                        read «۱,۳۲۰ تومان تومان». */}
                    <span className="pos-num" style={{ fontSize: 15, fontWeight: 700 }}>
                      {toman(a.totalRial)}
                    </span>
                  </div>

                  {(u.label || taken) && (
                    <div style={{
                      display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap',
                      marginTop: 4, fontSize: 13,
                    }}>
                      {u.label && (
                        <span style={{ color: u.border, fontWeight: 700 }}>{u.label}</span>
                      )}
                      {taken && (
                        /*
                         * Named only when there IS a name. `takenBy` is a user
                         * id and a till with no signed-in cashier claims with
                         * an empty one, which rendered a dangling dash in front
                         * of the time -- so the passive voice is used instead of
                         * printing nothing where a person's name belongs.
                         */
                        <span style={{ color: 'var(--luma-muted, #6C7276)' }}>
                          {(() => {
                            const who = (a.takenBy ?? '').trim();
                            const mins = a.takenMinutes && a.takenMinutes > 0
                              ? `${fa(a.takenMinutes)} دقیقه پیش `
                              : '';
                            if (who) {
                              return mins ? `${who} — ${mins}دریافت کرد` : `${who} دریافت کرد`;
                            }
                            return `${mins}دریافت شد`;
                          })()}
                        </span>
                      )}
                    </div>
                  )}

                  <ul style={{ margin: '8px 0 0', paddingInlineStart: 18, fontSize: 14.5 }}>
                    {a.lines.map((l, i) => (
                      <li key={i} style={{ marginBottom: 2 }}>
                        {l.name} × {fa(l.quantity)}
                        {l.modifiers.length > 0 && (
                          <span style={{ color: 'var(--luma-muted, #6C7276)' }}>
                            {' — '}
                            {l.modifiers.map((m) => m.name).join('، ')}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                {/*
                  One button, and it says what it does rather than what it means.
                  "دریافت شد" is a claim of responsibility: the order leaves every
                  till's list in the shop, so two cashiers cannot both make it.
                */}
                {/*
                  A CLAIMED ORDER MUST STILL BE SENDABLE TO THE KITCHEN.
                  Both buttons used to vanish the moment somebody pressed «فقط
                  دریافت شد», which strands the order: claimed, visible, and with
                  no way left to make it. Our user hit exactly that — two paid
                  coffees sat claimed for eighteen minutes with nothing to press.
                  Only the CLAIM button is hidden once claimed, because claiming
                  twice means nothing; firing is the whole point of the screen.
                */}
                <div
                /*
                 * Stopped here so a press on a button does not also open the
                 * sheet behind it. Firing from the list has to stay a
                 * one-tap action — it is what a cashier does in a rush.
                 */
                onClick={(e) => e.stopPropagation()}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                {/*
                  Firing is the primary action and claiming is the quiet one.
                  Sending an order to be made is the stronger statement — a
                  cashier who has done it has certainly taken responsibility —
                  and it claims the order in the same act, so the two cannot
                  drift apart.
                */}
                {/*
                  Not offered on an order the kitchen already has.
                  Firing is idempotent, so a second press would be harmless to
                  the data and not to the shop: a cashier who presses it and
                  sees nothing happen goes and asks the kitchen about an order
                  that was never in doubt. These orders are on the list to be
                  SEEN, which is the whole reason they stopped being hidden.
                */}
                {!alreadyFired(a) && (
                  <button
                    onClick={() => onFire(a.id)}
                    disabled={busyId === a.id}
                    style={{
                      border: 'none', background: 'var(--luma-accent, #1C6B62)', color: '#fff',
                      borderRadius: 10, padding: '12px 20px', fontSize: 15,
                      cursor: busyId === a.id ? 'default' : 'pointer',
                      opacity: busyId === a.id ? 0.6 : 1, whiteSpace: 'nowrap',
                    }}
                  >
                    {busyId === a.id ? 'لطفاً صبر کنید…' : 'ارسال به آشپزخانه'}
                  </button>
                )}
                {/*
                  Claiming is for kiosk orders only.
                  A kiosk order is paid and unattended, so "I have this" stops
                  two cashiers making it twice. A waiter's round has a waiter
                  standing at the counter and firing it is already idempotent —
                  there is nobody to race. The agent has no claim for a round
                  either, so offering the button here would only produce «سفارشی
                  با این شناسه نیست».
                */}
                {a.kind !== 'table' && !taken && (
                <button
                  onClick={() => onTake(a.id)}
                  disabled={busyId === a.id}
                  style={{
                    border: '1px solid var(--luma-line, #D6D6CC)', background: 'transparent',
                    color: 'var(--luma-ink, inherit)', borderRadius: 10,
                    padding: '9px 20px', fontSize: 14,
                    cursor: busyId === a.id ? 'default' : 'pointer',
                    opacity: busyId === a.id ? 0.6 : 1, whiteSpace: 'nowrap',
                  }}
                >
                  فقط دریافت شد
                </button>
                )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {staleUnclaimed > 0 && (
        /*
         * A footnote, not a row.
         *
         * Deliberately outside the scrolling list and without an action: these
         * are unclaimed orders from before this shift, and the reason they are
         * off the list is that a queue permanently full of last week is a queue
         * a cashier stops reading. Stating the number keeps them answerable —
         * they are all in the panel — without letting them compete with food
         * that needs making now.
         */
        <div
          style={{
            flex: 'none', fontSize: 13, color: 'var(--luma-muted, #6C7276)',
            borderTop: '1px solid var(--luma-line, #D6D6CC)', paddingTop: 10,
          }}
        >
          {`${fa(staleUnclaimed)} سفارش دریافت‌نشده از پیش از این شیفت — در پنل قابل بررسی است`}
        </div>
      )}
    </div>
  );
}
